import { type ReactNode } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { cn } from '@/utils/cn';
import type { Payment } from '../types';
import { formatUSD } from '../utils/payment-formatters';
import { PaymentCardHeader } from './payment-card-header';

type Props = { payment: Payment };

function buildBreakdown(p: Payment): string {
  const parts: string[] = [formatUSD(p.base_earnings)];
  if (p.bonus_earnings) parts.push(`${formatUSD(p.bonus_earnings)} ranking`);
  if (p.group_bonus_earnings)
    parts.push(`${formatUSD(p.group_bonus_earnings)} equipo`);
  if (p.race_bonus_eur) parts.push(`${formatUSD(p.race_bonus_eur)} velocidad`);
  return parts.join(' + ');
}

function CardBody({ p }: { p: Payment }): ReactNode {
  return (
    <>
      <PaymentCardHeader payment={p} />
      <p className="mt-sm text-sm font-bold text-text-primary">
        {formatUSD(p.total_earnings)}
      </p>
      <p className="mt-1 text-[10px] text-text-tertiary">{buildBreakdown(p)}</p>
      <p className="mt-1 text-[10px] text-text-secondary">
        {p.lead_count} creadores
      </p>
    </>
  );
}

// Fase: below-minimum payments are now payable (with a confirmation modal),
// so draggability is gated on a wallet being present — not on meets_minimum.
// No-wallet collectors (including admins, who never set one) stay disabled.
export function PaymentCard({ payment }: Props): ReactNode {
  const canDrag = payment.status === 'pending' && !!payment.usdc_wallet;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: payment.id,
    data: { payment },
    disabled: !canDrag,
  });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'rounded-md border border-white/[0.04] bg-surface-elevated p-sm transition-colors',
        canDrag &&
          'cursor-grab hover:border-white/[0.08] active:cursor-grabbing',
        !canDrag && payment.status === 'pending' && 'opacity-50',
        isDragging && 'cursor-grabbing opacity-30'
      )}
      {...(canDrag ? listeners : {})}
      {...(canDrag ? attributes : {})}
    >
      <CardBody p={payment} />
    </div>
  );
}
